import styled from "@emotion/styled";
import { NoSsr } from "@mui/base";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { InstagramEmbed, TwitterEmbed } from "react-social-media-embed";

import type { ContentSchemaRenderProps } from "~/components/content/content-schema-render";

import { InstagramContainer, StyledBlockquote, TweetContainer } from "./styles";

const types = {
  Blockquote: "blockquote",
  Instagram: "instagram",
  Twitter: "twitter",
};

const Component: FunctionComponent<ContentSchemaRenderProps> = ({
  children,
  reference: _content,
  ...attribs
}) => {
  /* cspell: disable-next-line */
  const instagramId = attribs.schemaNode?.attribs?.["data-instgrm-permalink"] ?? "";

  const tweetHasMedia = useMemo(() => {
    const hasPictureLink = (node: SchemaNode): boolean => {
      if (node.data?.startsWith("pic.twitter.com/")) {
        return true;
      } else if (node.children) {
        return node.children.some(child => hasPictureLink(child));
      }
      return false;
    };

    return hasPictureLink(attribs.schemaNode);
  }, [attribs.schemaNode]);

  const tweetUrl = useMemo(() => {
    const getTweetUrl = (node: SchemaNode): string => {
      if (node.subtype === "twitter") {
        return node.attribs?.href ?? "";
      } else if (node.children) {
        for (const [_, child] of node.children.entries()) {
          const url = getTweetUrl(child);
          if (url) return url;
        }
      }
      return "";
    };

    return getTweetUrl(attribs.schemaNode);
  }, [attribs.schemaNode]);

  const componentMap = {
    [types.Blockquote]: <StyledBlockquote {...attribs}>{children}</StyledBlockquote>,
    [types.Twitter]: (
      <TweetContainer $hasMedia={tweetHasMedia}>
        <TwitterEmbed url={tweetUrl} placeholderDisabled />
      </TweetContainer>
    ),
    [types.Instagram]: (
      <InstagramContainer>
        {/* Currently there is a SSR issue when using InstagramEmbed so require NoSSR for the time
            being - https://github.com/justinmahar/react-social-media-embed/issues/7 */}
        <NoSsr>
          <InstagramEmbed url={instagramId} width={326} />
        </NoSsr>
      </InstagramContainer>
    ),
  };

  const determineBlockquoteType = () => {
    if (attribs.schemaNode.attribs?.class === "twitter-tweet" && tweetUrl) return types.Twitter;
    if (attribs.schemaNode.subtype === "instagram" && instagramId) return types.Instagram;

    return types.Blockquote;
  };

  return componentMap[determineBlockquoteType()];
};

Component.displayName = "Blockquote";

export const Blockquote = styled(Component)``;
